import { useForm } from "react-hook-form";
import { FormModal, Input, Text } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { isPhoneNumber } from "../../lib/sms";
import {
  useMessageAction,
  type MessageDetail,
  type RedeliverRequest,
} from "../../lib/queries/messages";

interface RedeliverFormValues {
  to: { address: string; name: string };
}

/**
 * Redeliver from the message detail: a new copy with the same content,
 * addressed to the original recipient unless the admin changes it — a typo in
 * the address, or a customer who wants the mail at another mailbox. The field
 * names match the server's (`to.address`), so its 422 lands on the field.
 */
export function RedeliverModal({
  message,
  onClose,
  onDone,
}: {
  message: MessageDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useMessageAction();
  const isSMS = message.channel === "sms";
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RedeliverFormValues>({
    mode: "onTouched",
    defaultValues: {
      to: { address: message.to_address, name: message.to_name ?? "" },
    },
  });

  async function onSubmit(values: RedeliverFormValues) {
    const address = values.to.address.trim();
    const name = values.to.name.trim();
    // Unchanged, the request is the plain redelivery it always was.
    const changed = address !== message.to_address || name !== (message.to_name ?? "");
    const body: RedeliverRequest | undefined = changed
      ? { to: { address, ...(name ? { name } : {}) } }
      : undefined;
    try {
      // The stored address, not the typed one: the server normalizes it.
      const dup = await action.mutateAsync({ id: message.id, action: "redeliver", body });
      notifySuccess(
        dup.to_address === message.to_address
          ? "The message was queued for redelivery."
          : `A copy was queued for ${dup.to_address}.`,
      );
      onDone();
    } catch (err) {
      apiErrorToFormErrors<RedeliverFormValues>(err, setError, {
        fallbackMessage: "Redelivery failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="Redeliver"
      onClose={onClose}
      busy={action.isPending}
      error={rootErrorMessage(errors)}
      submitLabel="Redeliver"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Text>
        A new message with the same content will be created and queued. Change the recipient to
        send the copy to someone else.
      </Text>
      <Input
        label={isSMS ? "Phone number" : "Email address"}
        type={isSMS ? "tel" : "email"}
        required
        hint="The content is copied as it is, so anything in it written for the original recipient stays. An unsubscribe link in it unsubscribes the new recipient."
        error={errors.to?.address?.message}
        {...register("to.address", {
          required: "Required field",
          validate: (v) => !isSMS || isPhoneNumber(v) || "Must be a phone number in E.164 form",
        })}
      />
      <Input label="Name" error={errors.to?.name?.message} {...register("to.name")} />
    </FormModal>
  );
}
